import {BaseService} from './base';
import {addAuthorizationHeaderIfPresent, appDebug, getIdForUser, isMobile, iOS, testLogging, noop, promiseSingleton} from '../util';
import emitter from '../util/emitter';
import getAppVersion from '../util/getAppVersion';
import dialog from '../dialog';


const moduleDebug = appDebug.extend('apiService');

function isTagListBody(
    body: api.UnknownBody): body is api.TagListBody { // eslint-disable-line @typescript-eslint/no-unused-vars
    return 'results' in body && typeof body.results[0] === 'string';
}

function isMedia(obj: any): obj is api.Media {
    return 'url' in obj;
}

function isMediaFormats(obj: any): obj is api.MediaFormats {
    return obj && 'gif' in obj;
}

function isCollectionListBody(
    body: api.UnknownBody): body is api.CollectionListBody {
    return 'collections' in body;
}

function isPostListBody(body: api.UnknownBody): body is api.PostListBody {
    return 'results' in body && body.results[0] && body.results[0]['id'];
}

export class ResponseAndBodyError extends Error {
    response: Response|null;
    body: any;

    constructor(response: Response|null, body: any, cause: Error) {
        super('Error occurred during fetch.', {cause});
        this.response = response;
        this.body = body;
    }
}

export type ResponseAndBody<T> = {response: Response, body: T};

/**
 * Converts xhr to an object like we would get from fetch.
 */
function xhrToFetchResponse(xhr: XMLHttpRequest): Response {
    const headers = new Headers();
    xhr.getAllResponseHeaders().trim().split(/[\r\n]+/).forEach((line) => {
        if (line) {
            const [header, val] = line.split(': ', 2);
            headers.append(header, val);
        }
    });

    return new Response(xhr.responseText, {
        headers,
        status: xhr.status,
        statusText: xhr.statusText,
    });
}

/**
 * This callback type is called `requestCallback` and is displayed as a global
 * symbol.
 */
type onprogressCallback = (
    ev: ProgressEvent<EventTarget>,
    ) => any;

type XhrFetchParams = {
    url: string;
    method: string;
    data: File;
    headers: Headers;
    progressCB?: onprogressCallback;
};

function xhrFetch({url, method, data, headers, progressCB}: XhrFetchParams):
    Promise<Response> {
    return new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        // https://g3doc.corp.google.com/uploader/g3doc/customer/customer_config.md?cl=head#enable-multipart-parsing
        xhr.upload.onprogress = progressCB;
        xhr.onload = () => {
            resolve(xhrToFetchResponse(xhr));
        };
        xhr.onerror = (err: ProgressEvent) => {
            reject(new Error('API error', {cause: err}));
        };
        xhr.open('POST', url);
        for (const [key, val] of headers.entries()) {
            xhr.setRequestHeader(key, val);
        }
        try {
            xhr.send(data);
        } catch (err) {
            reject(new Error('Send error', {cause: err}));
        }
    });
}

function cleanGifListResponse({body, response}: ResponseAndBody<any>):
    ResponseAndBody<any> {
    if (isPostListBody(body)) {
        body = body as api.PostListBody;
        const ary = [];

        loop1:
        for (const gif of body.results) {
            if (isMediaFormats(gif.media_formats)) {
                // delete unused attributes to save on memory/page size
                delete gif.sticker;
                delete gif.content_rating;
                delete gif.composite;
                delete gif.primary_term;
                const mediaF = gif.media_formats as api.MediaFormats;
                for (const [format, media] of Object.entries(mediaF)) {
                    if (!isMedia(media)) {
                        continue;
                    }
                    if (!media.dims) {
                        console.warn(
                            `${format} missing dims, skipping gif: ${gif.id}`);
                        continue loop1;
                    }
                }
                // Fix for missing tinygif in v2.
                if (!mediaF.tinygif) {
                    mediaF.tinygif = mediaF.mediumgif || mediaF.gif;
                }
                if (mediaF.tinygif && !mediaF.tinywebp) {
                    mediaF.tinywebp = {...mediaF.tinygif};
                }
                if (mediaF.tinygif_transparent &&
                        !mediaF.tinywebp_transparent) {
                    mediaF.tinywebp_transparent = {
                        ...mediaF.tinygif_transparent};
                }
                ary.push(gif);
            }
        }
        body.results = ary;
    }

    return {body, response};
}

function getComponent(): string {
    return (iOS() ?
        'web_mobile_ios' : isMobile() ?
            'web_mobile' : 'web_desktop');
}

export function setFlags(body: Record<string, boolean>, flags: string[]) {
    for (const flag of flags) {
        if (flag) {
            body[flag.replace('!', '')] = !flag.startsWith('!');
        }
    }
}

function fixResponse<ResponseBodyType>(resolve, reject) {
    return function fixResponseInner(
        response: Response): Promise<ResponseAndBody<ResponseBodyType>> {
        // Our API doesn't return the correct HTTP status
        // codes on error so we need to check "successful"
        // API calls for errors.
        return response.text().then(function fixResponseTextThen(
            bodyStr: string) {
            let body = {body: bodyStr} as api.UnknownBody;
            let cause;
            if (bodyStr.length && response.headers.get(
                'content-type').startsWith('application/json')) {
                try {
                    body = JSON.parse(bodyStr) as api.UnknownBody;
                } catch (err) {
                    cause = err;
                    body = {
                        error: `JSON parsing failed: ${err}`,
                    } as api.ErrorBody;
                }
                if ((body as api.ErrorBody).error) {
                    reject(new ResponseAndBodyError(response, body, cause));
                    return;
                }
            }
            if (response.ok) {
                resolve({
                    response,
                    body: body as ResponseBodyType,
                });
            } else {
                // NOTE: (v2 API): need to ensure proper error handling for
                // future endpoints v2 has the correct HTTP status codes on
                // error but returns a different error object in the
                // body --> error: {code, status, message, details}
                if ((body as api.NonStandardErrorBody).error?.message) {
                    body = {
                        error: (
                            body as api.NonStandardErrorBody).error.message,
                    } as api.ErrorBody;
                }
                reject(new ResponseAndBodyError(response, body, cause));
            }
        });
    };
}

const fields = [
    'id',
    'media_formats',
    'title',
    'h1_title',
    'long_title',
    'itemurl',
    'url',
    'created',
    'user',
    'shares',
    'embed',
    'hasaudio',
    'policy_status',
    'source_id',
    'flags',
    'tags',
    'content_rating',
    'bg_color',
    'legacy_info',
    'geographic_restriction',
    'content_description',
].map((str: string) => {
    return `results.${str}`;
});

export class APIService extends BaseService {
    anonid?: string;
    cachedConfig: Record<string, api.Config>;
    cachedAppConfig: Record<string, api.AppConfig>;
    apirefid: string|null;
    isLowQuality: string;
    uploadGifV2XMLHttpRequest: Function;
    uploadProfileImageV2XMLHttpRequest: Function;
    updateProfileV2: Function;
    getUploadedGifsV2: Function;
    getListUploadsV2: Function;
    getBatchedListUploadsV2: Function;
    setFavorite: Function;
    setFavoriteV2_: Function;
    setFavoriteV2: Function;
    getUserLikedGifs: Function;
    fetchFavorites: Function;
    getV2FavoritesCollectionId_: Function;
    removeGif: Function;
    getProfile: Function;
    setBanner: Function;
    setAvatar: Function;
    getConfig_: Function;
    getConfig: Function;
    getAppConfig_: Function;
    getV2Notifications: Function;
    getProfilesV2: Function;
    createProfileV2: Function;
    enableConfigFlags: string[];
    enableAppConfigFlags: string[];
    favoritesCollectionId: null|string;
    anonidPromise: Promise<void>;
    baseV2APIQuery: app.URLParamsObj;
    LIMIT = 50;
    postResultFieldsStr = fields.join(',');
    postResultsFieldsStr = ['next', ...fields].join(',');
    mediaFilterStr = [
        'gif',
        'gif_transparent',
        'mediumgif',
        'tinygif',
        'tinygif_transparent',
        'webp',
        'webp_transparent',
        'tinywebp',
        'tinywebp_transparent',
        'tinymp4',
        'mp4',
        'webm',
        'originalgif',
        'gifpreview',
    ].join(',');

    constructor(context: jsx.AppGlobals) {
        super(context, {});

        const CONSTANTS = context.CONSTANTS;
        this.cachedConfig = {};
        this.cachedAppConfig = {};
        this.apirefid = null;
        this.isLowQuality = '';
        this.newApirefid();

        const query = new URLSearchParams(
            window?.location?.search);
        this.enableConfigFlags =
            query.get('enableConfigFlags')?.split(',') || [];
        this.context.request.console.debug(
            'enableConfigFlags', this.enableConfigFlags);
        this.enableAppConfigFlags =
            query.get('enableAppConfigFlags')?.split(',') || [];
        this.context.request.console.debug(
            'enableAppConfigFlags', this.enableAppConfigFlags);

        this.baseV2APIQuery = {
            appversion: getAppVersion(CONSTANTS),
            prettyPrint: 'false',
        };
        if (context?.request?.restrictedCountryCode) {
            this.baseV2APIQuery.country = context.request.restrictedCountryCode;
        }
        this.baseV2APIQuery.key = CONSTANTS.API_V2_KEY;
        this.baseV2APIQuery.client_key = CONSTANTS.API_V2_CLIENT_KEY;
        if (context.gtService?.gt?.locale) {
            this.baseV2APIQuery.locale = context.gtService.gt.locale;
        }

        // TODO: maybe move favoritesCollectionId to auth or favorites code.
        this.favoritesCollectionId = null;
        const resetFavoritesCollectionId = () => {
            if (!context.authService.authData) {
                this.favoritesCollectionId = null;
            }
        };
        process.env.BROWSER && emitter.on('authDataSet',
            resetFavoritesCollectionId);

        const {
            resolve: setupResolve,
            promise: anonidPromise,
        } = Promise.withResolvers<void>();
        const setupReject = function setupReject() {
            context.request.console.error('fetching anonid failed.');
            // We need to continue on as normal.
            setupResolve();
        };
        this.anonidPromise = anonidPromise.then(() => {
            if (this.anonid) {
                // This log line needed for tests.
                testLogging('anonidSet');
                // NOTE: Don't include on authenticated calls.
                this.baseV2APIQuery.anon_id = this.anonid;
            }
        }, function anonidPromiseCatch() {
            context.request.console.error(
                'An error occurred during API setup.');
        });

        if (process.env.BROWSER) {
            this.anonid = context.storageService.getItem('anonid');
            if (this.anonid) {
                setupResolve();
            } else {
                context.httpService.request({
                    method: 'GET',
                    url: `${CONSTANTS.API_V2_URL}/anonid`,
                    params: this.baseV2APIQuery,
                    headers: new Headers(),
                })
                    .then((response: Response) => {
                        if (response.ok) {
                            response.json().then((body) => {
                                this.anonid = body.anon_id;
                                context.storageService.setItem(
                                    'anonid', this.anonid);
                                setupResolve();
                            }, setupReject);
                        } else {
                            setupReject();
                        }
                    }, setupReject);
            }
        } else {
            setupResolve();
        }
        this.addMethods();
    }
    /**
     * @param {string} reason The reason lowQuality was detected.
     */
    setIsLowQualityToTrue(reason: string) {
        if (!this.isLowQuality) {
            this.isLowQuality = reason;
            window.setGaSessionDimension({
                isLowQuality: reason,
            });
        }
    }
    combineQueries(
        headers: Headers, path: string, ...queries: any[]): app.URLParamsObj {
        const query = Object.assign({}, this.baseV2APIQuery, ...queries);

        // NOTE: For event logging, we must remove the ability to
        // associate Tenor user identifiers (userid) to anonid. For
        // "/registerevent" requests, the isUserLoggedIn parameter
        // maps to userid when a user is logged in or an empty
        // string if not.
        if (query.data && path === '/registerevent') {
            const queryData = JSON.parse(query.data);
            queryData.isUserLoggedIn && delete query.anon_id;
        }
        if (query['upload_profile_id'] &&
                path === '/registerevent') {
            delete query.anon_id;
        }

        if (query.access_token || headers.get('authorization')) {
            delete query.anon_id;
        }
        return query;
    }
    getAnonid() {
        return this.anonid || '';
    }
    newApirefid() {
        if (process.env.BROWSER && window?.crypto?.randomUUID) {
            this.apirefid = window.crypto.randomUUID();
        }
    }
    addAuthToImgUrlIfV2(url: string|undefined): string|undefined {
        if (url?.includes('/v2/')) {
            url += `${url.includes('?') ? '&' : '?'}${new URLSearchParams({
                access_token: this.context.authService.getGoogleAccessToken(),
                key: this.context.CONSTANTS.API_V2_KEY,
            })}`;
        }
        return url;
    }
    /**
     * Make V2 API request.
     */
    requestV2<ResponseBodyType extends api.UnknownBody=api.UnknownBody>(
        method: string,
        path: string,
        params?: object,
        body?: object|null,
        headers?: Headers,
        tryBeacon?: boolean): Promise<ResponseAndBody<ResponseBodyType>> {
        const requestV2Promise = (resolve, reject) => {
            const requestV2AfterAnonid = () => {
                headers = headers || new Headers();
                let bodyStr = null;
                if (body) {
                    bodyStr = JSON.stringify(body);
                } else if (method !== 'GET') {
                    headers.set('content-type',
                        'application/x-www-form-urlencoded');
                }
                params = this.combineQueries(headers, path, params || {});

                const url = this.context.CONSTANTS.API_V2_URL + path;

                this.context.httpService.request({
                    method,
                    url,
                    params,
                    body: bodyStr,
                    headers,
                    tryBeacon,
                })
                    .then(fixResponse<ResponseBodyType>(resolve, reject),
                        (error: Error) => {
                            // Error occurred during the request.
                            this.context.request.console.error(error);
                            reject(error);
                        });
            };
            this.anonidPromise.then(requestV2AfterAnonid);
        };
        const p = new Promise<
            ResponseAndBody<ResponseBodyType>>(requestV2Promise);
        return p.then(cleanGifListResponse);
    }
    /**
     * Make V2 API get request.
     * @returns {Promise<ResponseAndBody<api.UnknownBody>>} Promise object
     * represents the response.
     */
    getV2<ResponseBodyType extends api.UnknownBody=api.UnknownBody>(
        path: string,
        params?: object,
        headers?: Headers): Promise<ResponseAndBody<ResponseBodyType>> {
        return this.requestV2<ResponseBodyType>(
            'GET', path, params, null, headers);
    }
    /**
     * Make V2 API post request.
     * @returns {Promise<ResponseAndBody<api.UnknownBody>>} Promise object
     * represents the response.
     */
    postV2<ResponseBodyType extends api.UnknownBody=api.UnknownBody>(
        path: string,
        params?: object,
        body?: object|null,
        headers?: Headers,
        tryBeacon?: boolean): Promise<ResponseAndBody<ResponseBodyType>> {
        return this.requestV2<ResponseBodyType>(
            'POST', path, params, body, headers, tryBeacon);
    }
    /**
     * Make V2 API patch request.
     * @returns {Promise<ResponseAndBody<api.UnknownBody>>} Promise object
     * represents the response.
     */
    patchV2<ResponseBodyType extends api.UnknownBody=api.UnknownBody>(
        path: string,
        params: object,
        body?: object|null,
        headers?: Headers): Promise<ResponseAndBody<ResponseBodyType>> {
        return this.requestV2<ResponseBodyType>(
            'PATCH', path, params, body, headers);
    }
    /**
     * Pre-emptively checks for token expiration or being logged out. If
     * auth fails we try to refresh the token and retry the request.
     */
    retryAfterAuthBase({f, requireAuth, requireTenorScope}: {
    f: Function, requireAuth: boolean, requireTenorScope: boolean}): Function {
        return (...args) => {
            let p;
            const authService = this.context.authService;
            if (authService.isLoggedIn()) {
                // If we have authData present, we need to make sure the
                // Google Auth libs have loaded before trying to check scope.
                p = authService.initResolver.promise.then(() => {
                    if (requireTenorScope &&
                        !authService.hasTenorOAuthScope()) {
                        return new Promise((resolve) => {
                            authService.showScopeRefreshDialog(
                                {loggedinCallback: resolve});
                        });
                    } else {
                        return authService.checkGoogleAccessTokenExpiration();
                    }
                });
            } else if (requireAuth) {
                p = new Promise((resolve) => {
                    authService.showLoginDialog({loggedinCallback: resolve});
                });
            } else {
                p = Promise.resolve();
            }
            return p.then(() => {
                this.context.request.console.log(
                    'this.retryAfterAuthBase calling f first time', f, args);
                const p = f(...args);
                return p.catch((
                    errArgs: ResponseAndBodyError) => {
                    this.context.request.console.error(
                        'this.retryAfterAuthBase', errArgs);
                    const response = errArgs.response;
                    if (response.status === 401 || response.status === 403) {
                        // Prompt for scope refresh
                        // In callback, retry
                        return new Promise((resolve, reject) => {
                            this.context.request.console.log(
                                'this.retryAfterAuthBase calling showScopeRefreshDialog',
                            );
                            authService.showScopeRefreshDialog({
                                loggedinCallback: () => {
                                    this.context.request.console.log(
                                        'this.retryAfterAuthBase calling f again',
                                        f,
                                        args,
                                    );
                                    f(...args).then(resolve, reject);
                                }});
                        });
                    } else {
                        return Promise.reject(errArgs);
                    }
                });
            });
        };
    }

    /**
     * Pre-emptively checks for token expiration or being logged out. If
     * auth fails we try to refresh the token and retry the request.
     */
    retryAfterAuth(f: Function) {
        return this.retryAfterAuthBase({
            f,
            requireAuth: false,
            requireTenorScope: false,
        });
    }
    /**
     * Pre-emptively checks for missing auth and scope. If auth fails we try to
     * refresh the token and retry the request.
     */
    retryAfterAuthAndScopeRefresh(f: Function) {
        return this.retryAfterAuthBase({
            f,
            requireAuth: true,
            requireTenorScope: true,
        });
    }
    getAppConfig() {
        const currentAnonid = this.getAnonid();
        return this.getAppConfig_(currentAnonid);
    }
    /**
     * Make V2 API get request to /search.
     * @returns {Promise<ResponseAndBody<api.PostListBody>>} Promise
     * object represents the response.
     */
    getSearch(params: app.URLParamsObj): Promise<
        ResponseAndBody<api.PostListBody>> {
        params.component = getComponent();
        return this.getV2<api.PostListBody>(
            '/search', params).then(({body, response}) => {
            if ('results' in body) {
                for (const result of body.results) {
                    result.q = params.q;
                    result.searchfilter = params.searchfilter;
                }
            }
            return {body, response};
        });
    }
    /**
     * @returns {string} Comma seperated list of integers.
     */
    getActiveExpIds(): string {
        const config = this.cachedConfig[
            this.context.authService.getUsername()];
        const appConfig = this.cachedAppConfig[this.getAnonid()];
        const expIds = [];
        if (config?.exp_ids) {
            expIds.push(...config.exp_ids.split(','));
        }
        if (appConfig?.exp_ids) {
            expIds.push(...appConfig.exp_ids.split(','));
        }
        return expIds.join(',');
    }
    registerEvent(eventname: string,
        data: app.FlexibleURLParamsObj = {},
        skipIsUserLoggedIn?: boolean) {
        const params: app.FlexibleURLParamsObj = {
            eventname,
            component: getComponent(),
        };
        if (this.apirefid) {
            // This should be generated once each new page load.
            data.apirefid = this.apirefid;
        }
        params.current_uri = window?.location?.href;
        const expIds = this.getActiveExpIds();
        if (expIds.length) {
            params.exp_ids = expIds;
        }
        if (eventname.startsWith('upload')) {
            const id = this.context.authService.getId?.();
            params['upload_profile_id'] = id ? id.toString() : '';
        } else {
            if (!data.isUserLoggedIn && !skipIsUserLoggedIn) {
                const id = this.context.authService.getId?.();
                data.isUserLoggedIn = id ? id.toString() : '';
            }
        }

        if (data) {
            if (Object.hasOwn(data, 'timing_milliseconds')) {
                params.timing_milliseconds = data.timing_milliseconds;
                delete data.timing_milliseconds;
            }
            if (Object.hasOwn(data, 'api_version')) {
                params.api_version = data.api_version;
                delete data.api_version;
            }
            if (Object.hasOwn(data, 'partner_profile')) {
                params.partner_profile = data.partner_profile;
                delete data.partner_profile;
            }
            if (Object.hasOwn(data, 'usertype')) {
                params.usertype = data.usertype;
                delete data.usertype;
            }
            if (Object.hasOwn(data, 'searchfilter')) {
                params.searchfilter = data.searchfilter;
                delete data.searchfilter;
            }
            params.data = JSON.stringify(data);
        }
        return this.postV2('/registerevent', params, null, null, true);
    }
    trackGifMakerEvent({eventName, params = {}, gaParams = {}}: {
        eventName: string,
        params: Record<string, any>,
        gaParams: app.URLParamsObj,
    }) {
        // NOTE convert array/hash to underscore separated string
        for (const property in params) {
            if (Object.hasOwn(params, property)) {
                let obj = params[property];
                if (obj && typeof obj === 'object') {
                    if (!Array.isArray(obj)) {
                        obj = Object.keys(obj);
                    }
                    params[property] = obj.join('_');
                }
            }
        }

        if (Object.hasOwn(params, 'info')) {
            if (params['info'] === null || params['info'] === undefined) {
                delete params['info'];
            }
        }
        params['api_version'] = 'API_V2';
        if (params['rid']) {
            params['category'] = 'caption';
        }

        const fullGaParams = {
            action: eventName,
            category: 'GifMaker',
            ...gaParams,
        };
        if (!Object.hasOwn(fullGaParams, 'value') &&
            Object.hasOwn(params, 'viewindex')) {
            fullGaParams['value'] = params['viewindex'];
        }
        if (!Object.hasOwn(fullGaParams, 'label')) {
            if (Object.hasOwn(params, 'actions')) {
                fullGaParams['label'] = params['actions'];
            } else if (Object.hasOwn(params, 'category')) {
                fullGaParams['label'] = params['category'];
            }
        }

        this.registerEvent(eventName, params);
        window.gaEvent(fullGaParams);
    }
    trackSeeAllContentTap(mediaType: string, src: string) {
        const eventName = 'see_all_content_tap';
        const params = {
            info: mediaType,
            category: src,
        };
        this.registerEvent(eventName, params);
    }
    registerShare(gif: api.Post) {
        const params: app.URLParamsObj = {
            id: gif.id,
            component: getComponent(),
        };
        if (this.apirefid) {
            // This should be generated once each new page load.
            params.apirefid = this.apirefid;
        }
        const expIds = this.getActiveExpIds();
        if (expIds.length) {
            params.exp_ids = expIds;
        }
        if (gif.source_id) {
            params.source_id = gif.source_id;
        }
        if (gif.searchfilter) {
            params.searchfilter = gif.searchfilter;
        }
        if (gif.q) {
            params.q = gif.q;
        }
        if (Number.isInteger(gif.index)) {
            params.index = gif.index.toString();
        }
        return this.postV2('/registershare', params, null, null, true);
    }
    registerPixel(url: string) {
        return fetch(url);
    }
    getAutocompleteResults(value: string, profileLimit?: number) {
        profileLimit = profileLimit || 0;
        const params = {
            q: value,
            type: 'trending',
            profile_limit: profileLimit,
        };
        return this.getV2<api.TagListBody>('/autocomplete', params);
    }
    // Sign IN to Tenor with a linked Google account
    googleLoginApiCall(data) {
        moduleDebug('googleLoginApiCall', data);
        return this.getProfilesV2(data.accessToken).then(({body, response}) => {
            if (!body.users.length) {
                const errMsg = 'No Profile';
                return Promise.reject(new ResponseAndBodyError(
                    response,
                    {error: errMsg},
                    new Error(errMsg),
                ));
            }
            const user = body.users[0];
            return {body: {user}};
        });
    }
    // Sign UP for a Tenor account and link to a Google account
    googleSignupApiCall(data) {
        moduleDebug('googleSignupApiCall', data);
        const postBody: app.URLParamsObj = {
            username: data.username,
            tos: data.tos,
            gaia_token: data.gaiaToken,
        };
        if (data.apitos) {
            postBody['apitos'] = 'true';
        }
        return this.getProfilesV2(data.accessToken).then(({body}) => {
            if (!body.users.length) {
                return this.createProfileV2(
                    data.username, data.accessToken);
            }
            const user = body.users[0];
            return {body: {user}};
        });
    }
    // Get a token for a linked account that was authed via
    // username/password. Also happens during scope-refresh.
    googleGetTokenForLegacyAuth(data): Promise<{user: api.User}[]> {
        const id = this.context.authService.getId();
        const functionDebug = moduleDebug.extend(
            'googleGetTokenForLegacyAuth');
        functionDebug('googleGetTokenForLegacyAuth', data);
        return this.getProfilesV2(data.accessToken).then(({body}) => {
            const user = body.users[0];
            const newId = getIdForUser(user);
            functionDebug(
                'googleGetTokenForLegacyAuth then', id, newId, body);
            if (!user || id && id !== newId) {
                // If no users were found, or if the IDs don't match.
                const gettextSub = this.context.gtService.gettextSub;
                return this.context.googleAuthService.signOut().then(() => {
                    // Throw this error *after* signing out so that we
                    // don't show the accountIdentifier for the wrong
                    // account. In reality though, signing out doens't
                    // happen quickly enough.
                    throw new Error(
                        gettextSub('Accounts do not match.'));
                });
            }
            return {body: {user}};
        });
    }

    getApiUploadBaseUrl(): string {
        return this.context.CONSTANTS.API_V2_URL.replace('/v2', '/upload/v2');
    }

    addMethods() {
        /**
         * Make v2 upload gif request.
         */
        this.uploadGifV2XMLHttpRequest = this.retryAfterAuthAndScopeRefresh(
            ({params, progressCB}: {
                    params: app.PostBody, progressCB: onprogressCallback
                }): Promise<ResponseAndBody<api.StrBody>> => {
                const path = '/upload';
                const uploadGifV2XMLHttpInner = (resolve, reject) => {
                    const uploadGifV2XMLHttpRequestAfterAnonid = () => {
                        const authService = this.context.authService;
                        const headers = new Headers();
                        const googleAccessToken =
                            authService.getGoogleAccessToken();
                        addAuthorizationHeaderIfPresent(headers,
                            googleAccessToken);

                        const data = params['file'] as File;
                        delete params['file'];

                        const newParams: app.URLParamsObj = this.combineQueries(
                            headers, path, params, {
                                profile_id: authService.getId(),
                            });

                        newParams['title'] = '';

                        const url = `${this.getApiUploadBaseUrl()}${path}?${
                            new URLSearchParams(newParams)}`;

                        const requestPromise = xhrFetch({
                            url,
                            method: 'POST',
                            data,
                            headers,
                            progressCB,
                        });

                        requestPromise.then(
                            fixResponse<api.StrBody>(resolve, reject),
                            (error) => {
                                this.context.request.console.error(error);
                                reject(error);
                            });
                    };
                    this.anonidPromise.then(
                        uploadGifV2XMLHttpRequestAfterAnonid);
                };
                return new Promise<ResponseAndBody<api.StrBody>>(
                    uploadGifV2XMLHttpInner);
            });
        this.uploadProfileImageV2XMLHttpRequest =
            this.retryAfterAuthAndScopeRefresh((
                {type, data, progressCB}: {
                    type: string, data: File, progressCB: onprogressCallback
                }): Promise<ResponseAndBody<api.StrBody>> => {
                const path = '/upload_profile_image';
                const uploadProfileImageV2XMLHttpRequestPromise = (
                    resolve, reject) => {
                    const uploadProfileImageAfterAnonid = () => {
                        const headers = new Headers();
                        const googleAccessToken =
                        this.context.authService.getGoogleAccessToken();
                        addAuthorizationHeaderIfPresent(headers,
                            googleAccessToken);
                        const params = this.combineQueries(headers, path, {
                            profile_id: this.context.authService.getId(),
                        });
                        params.profile_image_type = type;

                        const url = `${this.getApiUploadBaseUrl()}${path}?${
                            new URLSearchParams(params)}`;

                        const requestPromise = xhrFetch({
                            url,
                            method: 'POST',
                            data,
                            headers,
                            progressCB,
                        });

                        requestPromise.then(
                            fixResponse<api.StrBody>(resolve, reject),
                            (error) => {
                                this.context.request.console.error(error);
                                reject(error);
                            });
                    };
                    this.anonidPromise.then(uploadProfileImageAfterAnonid);
                };
                return new Promise<ResponseAndBody<api.StrBody>>(
                    uploadProfileImageV2XMLHttpRequestPromise);
            });
        this.updateProfileV2 = this.retryAfterAuthAndScopeRefresh((data) => {
            this.context.request.console.log('updateProfileV2', data);
            const query = {};
            const headers = new Headers();
            const googleAccessToken =
                this.context.authService.getGoogleAccessToken();
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            const keys = Object.keys(data);
            if (!keys.length) {
                return Promise.resolve(new Error('`data` is empty.'));
            }
            const body = {
                profile_id: this.context.authService.getId(),
                update_mask: keys.join(','),
            };
            body.update_mask = body.update_mask.replace(
                'partnercta', 'partnercta.url,partnercta.text');
            for (const [key, val] of Object.entries(data)) {
                body[key] = val;
            }
            return this.patchV2('/update_profile', query, body, headers);
        });
        this.getUploadedGifsV2 = this.retryAfterAuthAndScopeRefresh((
            ids: string[]) => {
            moduleDebug('getUploadedGifsV2', ids);
            const query = {
                profile_id: this.context.authService.getId(),
                ids,
                media_filter: this.mediaFilterStr,
                fields: this.postResultsFieldsStr,
            };
            const headers = new Headers();
            const googleAccessToken =
                this.context.authService.getGoogleAccessToken();
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            return this.getV2<api.PostListBody>(
                '/list_uploads', query, headers);
        });
        this.getListUploadsV2 = this.retryAfterAuthAndScopeRefresh((
            pos: string) => {
            const query: app.URLParamsObj = {
                profile_id: this.context.authService.getId(),
                media_filter: this.mediaFilterStr,
                fields: this.postResultsFieldsStr,
            };
            if (pos) {
                query.pos = pos;
            }
            const headers = new Headers();
            const googleAccessToken =
                this.context.authService.getGoogleAccessToken();
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            return this.getV2<api.PostListBody>(
                '/list_uploads', query, headers);
        });
        this.getBatchedListUploadsV2 = promiseSingleton((pos: string) => {
            return new Promise((resolve) => {
                let results = [];
                const listUploadsLoop = () => {
                    this.getListUploadsV2(pos).then(({body, response}) => {
                        results = body.results = results.concat(
                            body.results);
                        if (body.next !== '' && results.length < 100) {
                            pos = body.next;
                        } else {
                            resolve({body, response});
                            return;
                        }
                        listUploadsLoop();
                    });
                };
                listUploadsLoop();
            });
        });
        this.setFavoriteV2_ = this.retryAfterAuthAndScopeRefresh(
            (pid: string, remove: boolean) => {
                const headers = new Headers();
                const googleAccessToken =
                    this.context.authService.getGoogleAccessToken();
                addAuthorizationHeaderIfPresent(headers, googleAccessToken);

                const endpoint = remove ?
                    '/remove_collection_post' : '/add_collection_post';

                return this.getV2FavoritesCollectionId_().then(
                    (collectionId: string) => {
                        return this.postV2(endpoint, {
                            pid,
                            collection_id: collectionId,
                        }, null, headers);
                    },
                );
            },
        );

        this.setFavorite = (data: {
            pid?: string, gif?: api.Post, remove?: boolean}) => {
            let {pid, gif, remove} = data;
            moduleDebug('setFavorite', pid, gif, remove);
            if (gif) {
                pid = gif.id;
            }

            this.context.cacheService.updateFavorites(pid, remove);
            moduleDebug('setting favorite');
            const p = this.setFavoriteV2_(pid, remove);
            return p.then((body) => {}, (err: Error) => {
                /* remove 'like' if API call fails */
                this.context.cacheService.updateFavorites(pid, !remove);
                this.context.request.console.error(err);
            }).finally(() => {
                emitter.emit('favoriteDataSet');
            });
        };
        this.fetchFavorites = function fetchFavorites() {
            const favoritesList: Record<string, boolean> = {};
            const func = this.getUserLikedGifs;
            let callCount = 0;
            const cb = ({body}) => {
                callCount += 1;
                const data = body.results;
                data.forEach((result: api.Post) => {
                    favoritesList[result.id] = true;
                });

                // NOTE (v2 API): when updating to v2/userliked, "next" may
                // be an empty string instead of 0.
                if (!data.length || body.next === '0' || body.next === 0 ||
                    body.next === '' || callCount > 10) {
                    this.context.cacheService.setFavorites(favoritesList);
                    emitter.emit('favoriteDataSet');
                    return;
                }
                func(body.next).then(cb, (err: Error) => {
                    this.context.request.console.error(err);
                });
            };
            func().then(cb, (err: Error) => {
                this.context.request.console.error(err);
            });
        };
        this.getV2FavoritesCollectionId_ = promiseSingleton(
            this.retryAfterAuthAndScopeRefresh(() => {
                if (this.favoritesCollectionId) {
                    return Promise.resolve(this.favoritesCollectionId);
                }
                const query = {
                    profile_id: this.context.authService.getId(),
                };
                const headers = new Headers();
                const googleAccessToken =
                    this.context.authService.getGoogleAccessToken();
                addAuthorizationHeaderIfPresent(headers, googleAccessToken);
                return this.getV2<api.CollectionListBody>('/collections', query, headers).then(
                    ({body}) => {
                        if (isCollectionListBody(body)) {
                            const favoritesCollection = body.collections[0];
                            this.favoritesCollectionId =
                                favoritesCollection.collection_id;
                            return this.favoritesCollectionId;
                        }
                    });
            }));
        /**
         *
         * @param {string} pos
         * @returns {Promise<ResponseAndBody<PostListBody>>}
         */
        this.getUserLikedGifs = promiseSingleton(
            this.retryAfterAuthAndScopeRefresh((pos) => {
                const p = this.getV2FavoritesCollectionId_().then((
                    collectionId: string) => {
                    const query: app.URLParamsObj = {
                        collection_id: collectionId,
                        fields: this.postResultsFieldsStr,
                    };
                    const headers = new Headers();
                    const googleAccessToken =
                    this.context.authService.getGoogleAccessToken();
                    addAuthorizationHeaderIfPresent(headers,
                        googleAccessToken);
                    if (pos) {
                        if (!/^\d+$/.test(pos)) {
                            const errMsg = `${pos} (${
                                typeof pos}) is not a valid pos`;
                            return Promise.reject(new ResponseAndBodyError(
                                null,
                                {
                                    error: errMsg,
                                },
                                new Error(errMsg),
                            ));
                        }
                        query.pos = pos;
                    }
                    return this.getV2<api.PostListBody>(
                        '/collection_posts', query, headers);
                });
                return p;
            }));
        this.removeGif = this.retryAfterAuthAndScopeRefresh((
            gif: api.Post) => {
            const query = {
                id: gif.id,
            };
            const headers = new Headers();
            const googleAccessToken =
                this.context.authService.getGoogleAccessToken();
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            return this.postV2(
                '/delete_post', query, null, headers).then(() => {
                const gettextSub = this.context.gtService.gettextSub;
                dialog.open('popup-dialog', {
                    closeDelay: 2500,
                    children: (
                        <div>
                            <h1>{
                                gettextSub('Gif successfuly removed.')
                            }</h1>
                        </div>
                    ),
                });
            }, this.context.request.console.error);
        });
        this.getProfile = promiseSingleton(this.retryAfterAuthAndScopeRefresh(
            () => {
                const query = {};
                const headers = new Headers();
                const googleAccessToken =
                    this.context.authService.getGoogleAccessToken();
                addAuthorizationHeaderIfPresent(headers, googleAccessToken);
                const requestPromise = this.getV2<api.UserSingleBody>(
                    '/profile', query, headers).then(({body, response}) => {
                    return {body, response};
                });
                return requestPromise;
            }));
        this.setBanner = this.retryAfterAuth((data: File) => {
            return this.uploadProfileImageV2XMLHttpRequest(
                {type: 'banner', data});
        });
        this.setAvatar = this.retryAfterAuth((data: File) => {
            this.context.request.console.log('setAvatar', data);

            return this.uploadProfileImageV2XMLHttpRequest(
                {type: 'avatar', data});
        });
        this.getConfig_ = promiseSingleton(this.retryAfterAuth((
            username: string, googleAccessToken: string,
            hasTenorOAuthScope: boolean) => {
            const query = {};
            // Tenor scope is required for the token to work.
            // Otherwise, call it without any auth.
            const headers = new Headers();
            if (googleAccessToken && hasTenorOAuthScope) {
                addAuthorizationHeaderIfPresent(
                    headers, googleAccessToken);
            }
            return this.getV2<api.ConfigServerBody>('/config', query, headers).then(({body, response}) => {
                const newBody: api.Config = {
                    exp_ids: body.exp_ids,
                    flags: {},
                };
                for (const key of Object.keys(body)) {
                    if (body[key] === 'false') {
                        newBody.flags[key] = false;
                    } else if (body[key] === 'true') {
                        newBody.flags[key] = true;
                    }
                }
                setFlags(newBody.flags, this.enableConfigFlags);
                this.cachedConfig[username] = newBody;
                emitter.emit('configFetched',
                    [username, newBody, response]);
                return {body: newBody, response};
            });
        }));

        this.getConfig = function getConfig() {
            const username = this.context.authService.getUsername();
            const googleAccessToken =
                this.context.authService.getGoogleAccessToken();
            const hasTenorOAuthScope =
                this.context.authService.hasTenorOAuthScope();
            return this.getConfig_(
                username, googleAccessToken, hasTenorOAuthScope);
        };

        this.getAppConfig_ = promiseSingleton((currentAnonid: string) => {
            return this.getV2<api.AppConfigServerBody>('/app_config').then(({body, response}) => {
                const newBody: api.AppConfig = {
                    exp_ids: body.exp_ids,
                    flags: {},
                };
                for (const key of Object.keys(body)) {
                    if (!key.startsWith('web_') && key !== 'exp_ids') {
                        // Strip out unneeded flags.
                        continue;
                    }
                    if (body[key] === 'false') {
                        newBody.flags[key] = false;
                    } else if (body[key] === 'true') {
                        newBody.flags[key] = true;
                    }
                }
                console.debug('foo!!!!!!', body, newBody);
                setFlags(newBody.flags, this.enableAppConfigFlags);
                this.cachedAppConfig[currentAnonid] = newBody;
                emitter.emit(
                    'appConfigFetched', [currentAnonid, newBody, response]);
                return {body: newBody, response};
            });
        });

        this.getV2Notifications = promiseSingleton(
            this.retryAfterAuthAndScopeRefresh((
                next: number,
            ): Promise<ResponseAndBody<api.NotificationListBody>> => {
                const query = {
                    limit: 100,
                    profile_id: this.context.authService.getId(),
                    pos: next,
                };
                const headers = new Headers();
                const googleAccessToken =
                    this.context.authService.getGoogleAccessToken();
                addAuthorizationHeaderIfPresent(headers, googleAccessToken);
                return this.getV2<api.NotificationListBody>('/notifications', query, headers).then(
                    ({body, response}) => {
                        body.notifications.forEach(
                            (notification: api.Notification) => {
                                // The v2 API gives us a string but we
                                // expect a number.
                                if (typeof notification.timestamp === 'string') {
                                    notification.timestamp = parseInt(
                                        notification.timestamp);
                                }
                            });
                        return {body, response};
                    });
            }));
        this.getProfilesV2 = this.retryAfterAuth((
            googleAccessToken: string) => {
            const headers = new Headers();
            googleAccessToken = googleAccessToken ||
                this.context.authService.getGoogleAccessToken();
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            return this.getV2<api.UserListBody>('/profiles', {}, headers);
        });
        this.createProfileV2 = this.retryAfterAuth(
            (username: string, googleAccessToken: string) => {
                const headers = new Headers();
                addAuthorizationHeaderIfPresent(headers, googleAccessToken);
                return this.postV2<api.UserSingleBody>(
                    '/create_profile',
                    {username: username}, null, headers,
                );
            });
    }
}
